import type { TaxBracket } from '@/data/tax/2027'
import { brackets2027, rebates2027 } from '@/data/tax/2027'
import type { CalculatorContext } from '@/types/calculator'
import { roundCents } from '@/utils/money'
import type { SalaryIncreaseInputs, SalaryIncreaseResult } from './types'

function annualTax(gross: number): number {
  const bracket = brackets2027.find(
    (b: TaxBracket) => gross >= b.min && (b.max === null || gross <= b.max)
  )
  if (!bracket) return 0
  const tax = bracket.base + bracket.rate * (gross - bracket.min)
  return Math.max(0, tax - rebates2027.primary)
}

export function calculateSalaryIncrease(
  inputs: SalaryIncreaseInputs,
  _context: CalculatorContext
): SalaryIncreaseResult {
  const currentAnnualGross =
    inputs.salaryPeriod === 'monthly' ? inputs.currentSalary * 12 : inputs.currentSalary

  const annualIncrease = currentAnnualGross * (inputs.increasePercent / 100)

  const bonusAnnual =
    inputs.bonusType === 'percent'
      ? currentAnnualGross * (inputs.bonusValue / 100)
      : inputs.bonusValue

  const newAnnualGross = currentAnnualGross + annualIncrease

  let taxOnIncrease = 0
  let estimatedNetAnnualIncrease = annualIncrease
  let estimatedNetMonthlyIncrease = annualIncrease / 12

  if (inputs.includeTaxEstimate && inputs.annualGrossForTax > 0) {
    const baseTax = annualTax(inputs.annualGrossForTax)
    const newTax = annualTax(inputs.annualGrossForTax + annualIncrease + bonusAnnual)
    taxOnIncrease = Math.max(0, newTax - baseTax)
    estimatedNetAnnualIncrease = annualIncrease + bonusAnnual - taxOnIncrease
    estimatedNetMonthlyIncrease = estimatedNetAnnualIncrease / 12
  }

  return {
    currentAnnualGross: roundCents(currentAnnualGross),
    currentMonthlyGross: roundCents(currentAnnualGross / 12),
    newAnnualGross: roundCents(newAnnualGross),
    newMonthlyGross: roundCents(newAnnualGross / 12),
    annualGrossIncrease: roundCents(annualIncrease),
    monthlyGrossIncrease: roundCents(annualIncrease / 12),
    bonusAnnual: roundCents(bonusAnnual),
    bonusMonthly: roundCents(bonusAnnual / 12),
    taxOnIncrease: roundCents(taxOnIncrease),
    estimatedNetAnnualIncrease: roundCents(estimatedNetAnnualIncrease),
    estimatedNetMonthlyIncrease: roundCents(estimatedNetMonthlyIncrease),
    grossOnly: !inputs.includeTaxEstimate || inputs.annualGrossForTax <= 0,
  }
}
